import {Locale} from '@dms/api/base';
import {useGetConsentTypesQuery} from '@dms/api/customer';
import {OrderResponseBody} from '@dms/api/sales';
import {DocumentCode, useDocumentTemplates} from '@dms/shared';
import {LegalFormEnum, FileDataTypeEnum} from '@dms/teas';

import {buildArray} from 'shared/web';

const purchaseTemplateTypes: DocumentCode[] = [
  'vehicle-condition',
  'power-of-attorney',
  'handover-protocol',
  'purchase-contract',
];
const getSaleTemplateTypes = (isBrokeredSale: boolean): DocumentCode[] =>
  buildArray<DocumentCode>()
    .add('vehicle-condition')
    .add('power-of-attorney')
    .add('sale-handover-protocol')
    .whenNot(isBrokeredSale, 'sale-contract')
    .when(isBrokeredSale, 'sale-brokerage-contract');

const purchaseBrokerageTemplateTypes: DocumentCode[] = [
  'vehicle-condition',
  'power-of-attorney',
  'purchase-brokerage-handover-protocol',
  'purchase-brokerage-contract',
];

const consentTypeTemplateType = FileDataTypeEnum.GDPR_MARKETING_CONSENT;

type CheckoutDocumentTemplatesProps = {
  order: OrderResponseBody | undefined;
  isBrokeredSale: boolean;
};

type TemplateOption = {
  title: string;
  id: string;
  locale?: Locale | string | null;
  isPrimary?: boolean;
};

export type CheckoutTemplateReturnType = {
  id: string | undefined;
  title: string | undefined;
  type: string;
  templates: TemplateOption[];
  primaryTemplate: TemplateOption | undefined;
  authorizationTemplates?: TemplateOption[] | undefined;
  authorizationProfileLabel?: string | undefined;
};

export const useGetCheckoutDocumentTemplates = ({
  order,
  isBrokeredSale,
}: CheckoutDocumentTemplatesProps) => {
  const orderDiscriminator = order?.orderDiscriminator;
  const canShowConsentTemplates =
    order?.contractInformation?.customerContractInformation?.legalForm ===
    LegalFormEnum.NATURAL_PERSON;

  const [getTemplatesByCode, {isLoading: isTemplatesLoading, isError: isTemplatesError}] =
    useDocumentTemplates();
  const {
    data: consentTypes,
    isLoading: isConsentTypesLoading,
    isError: isConsentTypesError,
  } = useGetConsentTypesQuery(null, {skip: !canShowConsentTemplates});

  const isLoading = isTemplatesLoading || isConsentTypesLoading;
  const isError = isTemplatesError || isConsentTypesError;

  const orderTemplates = buildArray<DocumentCode>()
    .whenNot(
      orderDiscriminator === 'SALE' || orderDiscriminator === 'PURCHASE',
      ...purchaseBrokerageTemplateTypes
    )
    .when(orderDiscriminator === 'SALE', ...getSaleTemplateTypes(isBrokeredSale))
    .when(orderDiscriminator === 'PURCHASE', ...purchaseTemplateTypes);

  const consentTypeTemplates: CheckoutTemplateReturnType[] = (consentTypes ?? []).map(
    (consentType) => {
      const primaryTemplate = consentType.templates.find((template) => template.isPrimary);
      return {
        id: consentType.id,
        title: consentType.name,
        type: consentTypeTemplateType,
        primaryTemplate: primaryTemplate
          ? {
              id: primaryTemplate.fileId ?? '',
              title: primaryTemplate.title ?? '',
              locale: primaryTemplate.locale,
              isPrimary: primaryTemplate.isPrimary,
            }
          : undefined,
        templates: consentType.templates.map((template) => ({
          title: template.title,
          id: template.fileId ?? '',
          locale: template.locale,
          isPrimary: template.isPrimary,
        })),
      };
    }
  );

  const documentTemplates: CheckoutTemplateReturnType[] = orderTemplates.map((templateCode) => {
    const {
      definition,
      primaryTemplate,
      selectedTemplates,
      defaultTemplates,
      selectedAuthProfileLabel,
    } = getTemplatesByCode(templateCode);

    return {
      id: definition?.id,
      title: definition?.title,
      type: templateCode,
      templates: defaultTemplates,
      authorizationTemplates: selectedTemplates,
      authorizationProfileLabel: selectedAuthProfileLabel ?? undefined,
      primaryTemplate: primaryTemplate
        ? {
            id: primaryTemplate.id,
            title: primaryTemplate.title,
            locale: primaryTemplate.locale,
            isPrimary: primaryTemplate.isPrimary,
          }
        : undefined,
    };
  });

  const templates = [...documentTemplates, ...consentTypeTemplates];

  return {isLoading, isError, templates};
};
